<!-- Top right pill (computer only): active project with its menu, online-save indicator, the speaker of the app themes
     (only with a theme other than Default), profile button with the warning dot.
     The ids are used by the guided tour (#projectBtn, #profileBtn) and by src/features/cloud/autosync.ts (#syncBtn: a tap there is not the
     "renew Google" gesture). The project list (ProjectChoiceList.vue) is also shown in the Profiles window (ProfilesDialog.vue, #profileProjects). -->
<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import { usePopover } from '../composables/usePopover'
import ProfileAvatar from './ProfileAvatar.vue'
import SoundButton from './SoundButton.vue'
import ProjectChoiceList from './ProjectChoiceList.vue'
import { useThemeStore } from '../stores/theme'
import { useDataStore } from '../stores/data'
import { useProjectsStore } from '../stores/projects'
import { useUiStore } from '../stores/ui'
import { useI18n } from '../composables/useI18n'
import { safeColor } from '../utils/color'
import { syncBtnClick } from '../features/cloud/autosync'
import { useDialogsStore } from '../stores/dialogs'
import { useCloudStore } from '../stores/cloud'
import { app } from '../features/app'

const store = useDataStore()
const projects = useProjectsStore()
const ui = useUiStore()
const { t } = useI18n()
const cloud = useCloudStore()
const theme = useThemeStore()

const profile = computed(() => store.currentProfile())
// Projects and active project, once the app has started with a profile (before that the data must not be read).
// A new object at every save (store.rev) and legacy redraw (ui.tick): the data is changed in place.
const view = computed(() => {
  void store.rev; void ui.tick
  if (!app.booted || !profile.value) return null
  const d = store.getData()
  if (!d.projects.length) return null
  const active = projects.active()
  return { d, active, project: d.projects.find(p => p.id === active) || null }
})
const projectTitle = computed(() => (view.value?.project ? t('Project: {name}', { name: view.value.project.name }) : t('Project: all')))
function manageProjects() {
  projects.closeMenu()
  cloud.closeProfiles()
  useDialogsStore().show('projects', {})
}

// A click outside the menu closes it (the project button stops its own click; Esc is in src/features/keyboard.ts)
const menu = ref<HTMLElement | null>(null)
usePopover(toRef(projects, 'menuOpen'), menu)

const pillCls = 'rounded-full bg-darkcard border border-gray-800 shadow-sm items-center text-xs font-semibold text-gray-300 hover:text-gray-100 hover:border-brand/60 transition-colors'
</script>

<template>
  <div class="top-controls hidden md:flex fixed top-2 right-6 z-30 items-center gap-2 p-1.5 rounded-full border border-gray-800 shadow-xl">
    <!-- Main colour and light/dark theme are only in Settings › Appearance (user request) -->
    <div class="relative">
      <button id="projectBtn" type="button" :title="projectTitle" :class="[view ? 'flex' : 'hidden', 'h-8 px-2.5 md:px-3 gap-2 max-w-[8.5rem] md:max-w-[13rem]', pillCls]"
        @click.stop="projects.toggleMenu()">
        <span v-if="view?.project" class="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" :style="{ background: safeColor(view.project.color) }"></span>
        <i v-else class="fas fa-layer-group text-brand text-[11px]"></i>
        <span class="truncate">{{ view?.project ? view.project.name : t('All projects') }}</span>
        <i class="fas fa-chevron-down text-[9px] text-gray-500"></i>
      </button>
      <div id="projectMenu" ref="menu" :class="[projects.menuOpen ? '' : 'hidden', 'absolute right-0 top-10 w-72 max-w-[calc(100vw-2rem)] bg-darkcard border border-gray-800 rounded-2xl shadow-2xl p-2 z-40 modal-animate']">
        <ProjectChoiceList v-if="view && projects.menuOpen" @manage="manageProjects" />
      </div>
    </div>
    <!-- Online save status (syncView in src/features/cloud/autosync.ts → ui store): saved / saving / to save / offline / tap to resume -->
    <button id="syncBtn" type="button" :title="ui.sync?.tip" :aria-label="ui.sync?.tip"
      :class="[ui.sync ? 'flex' : 'hidden', 'h-8 min-w-[2rem] px-2 md:px-3 justify-center gap-1.5', pillCls]" @click="syncBtnClick()">
      <template v-if="ui.sync">
        <i :class="['fas', ui.sync.icon, ui.sync.cls, 'text-xs']"></i><span class="hidden md:inline">{{ ui.sync.txt }}</span>
      </template>
    </button>
    <!-- Sounds of the app themes: only while a theme other than Default is on (SoundButton.vue) -->
    <SoundButton v-if="theme.style !== 'default'" :btn-class="['h-8 w-8 justify-center flex', pillCls].join(' ')" />
    <!-- Active profile: opens the Profiles dialog -->
    <button id="profileBtn" :title="t('Profile: switch, edit, save or load')" :class="['relative h-8 pl-1 pr-1 md:pr-3 flex gap-2 max-w-[13rem]', pillCls]" @click="cloud.openProfiles()">
      <ProfileAvatar class="profileInitial w-6 h-6 text-[10px] flex-shrink-0" />
      <span class="profileName hidden md:inline truncate">{{ profile ? profile.name : t('My profile') }}</span>
      <i class="fas fa-chevron-down text-[9px] text-gray-500 hidden md:inline"></i>
      <!-- Warning dot: unsaved changes or online autosave paused (data.js refreshWarn → ui store) -->
      <span id="folderWarn" :title="ui.profileWarn || undefined" :class="[ui.profileWarn ? '' : 'hidden', 'absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-brand ring-2 ring-darkbg']"></span>
    </button>
  </div>
</template>
